import React, { useState } from 'react';
import { Card, Button, Space, Typography, Row, Col, message } from 'antd';
import { FileTextOutlined, BarChartOutlined, HomeOutlined, DownloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import assetService from '@/services/asset.service';

const { Title, Text } = Typography;

const reportLinks = [
  { to: '/asset/reports/movements', label: 'Movement log', desc: 'Issues, returns, transfers with filters and CSV', icon: <FileTextOutlined /> },
  { to: '/asset/reports/location', label: 'Location (by site/room)', desc: 'On-hand assets with location fields', icon: <HomeOutlined /> },
  { to: '/asset/depreciation', label: 'Depreciation', desc: 'Preview runs, posted book values (Finance)', icon: <BarChartOutlined /> },
  { to: '/asset/accounting-defaults', label: 'Accounting defaults', desc: 'GL accounts for depreciation, disposal, capitalization', icon: <BarChartOutlined /> },
  { to: '/asset/maintenance', label: 'Maintenance (PM/CM/AMC)', desc: 'Cases, schedules, history', icon: <FileTextOutlined /> },
  { to: '/asset/insurance', label: 'Insurance', desc: 'Policies and claims by asset', icon: <FileTextOutlined /> },
  { to: '/asset/disposal', label: 'Disposal', desc: 'Posted disposal events and GL links', icon: <FileTextOutlined /> },
  { to: '/asset/audit/report', label: 'Audit (summary + discrepancies)', desc: 'Stocktake cycles and missing/damaged export', icon: <FileTextOutlined /> },
] as const;

const AssetReportsPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.view'));
  const [regLoading, setRegLoading] = useState(false);

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>Access denied. Need asset.view</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 8 }}>
      <Title level={4} style={{ marginTop: 0 }}>
        Asset reports
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        Company-wide register download, movement history, and quick links to depreciation, maintenance, insurance, disposal, and audit.
      </Text>

      <Card title="Register export" style={{ marginBottom: 16 }} size="small">
        <Space direction="vertical">
          <Text>Download a full <strong>asset register</strong> CSV (all non-removed row for your company) for audits and handoffs.</Text>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={regLoading}
            onClick={async () => {
              setRegLoading(true);
              try {
                await assetService.downloadRegisterReportAsFile();
                message.success('Register CSV download started');
              } catch (e: any) {
                message.error(e?.message || 'Download failed');
              } finally {
                setRegLoading(false);
              }
            }}
          >
            Download asset register (CSV)
          </Button>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        {reportLinks.map((r) => (
          <Col xs={24} md={12} key={r.to}>
            <Link to={r.to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
              <Card size="small" hoverable>
                <Space>
                  {r.icon}
                  <div>
                    <Text strong>{r.label}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {r.desc}
                    </Text>
                  </div>
                </Space>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default AssetReportsPage;
